import React from 'react'

import { useShipmentContext } from '../../../../contexts/shipment-context'
import {
  AuthorizationStatusEnum,
  type IDeviceInfo,
  type TLocationSettingsType,
  type TMotionActivityType
} from '../../../../types/tracking'
import type { IPhoneNumberInfo } from '../../../../types/user'
import { dateTimeAgo } from '../../../../utils/datetime-utils'
import { ActiveDeviceView } from './active-device-view'
import { AlertSection } from './alert-section'
import { HistoricalDeviceView } from './historical-device-view'

interface MobileAppDeviceTooltipProps {
  device: IDeviceInfo | null
  driverName: string | null
  hasSentMockLocations: boolean
  isHistoricalDevice: boolean
  isLoggedIn?: boolean
  isTrackingOn?: boolean
  lastMotionActivity: TMotionActivityType | null
  lastSeen: string | null
  lastTrackingStoppedAt: string | null
  locationSettings: TLocationSettingsType | null
  phoneNumberInfo?: IPhoneNumberInfo | null
}

/**
 * The component that displays the device info and its tooltip
 */
export const MobileAppDeviceTooltip = React.memo(
  ({
    device,
    driverName,
    hasSentMockLocations,
    isHistoricalDevice,
    isLoggedIn,
    isTrackingOn,
    lastMotionActivity,
    lastSeen,
    lastTrackingStoppedAt,
    locationSettings,
    phoneNumberInfo = null
  }: MobileAppDeviceTooltipProps) => {
    // Safe version of useShipmentContext that doesn't throw
    let isShipmentOwner = false
    try {
      const context = useShipmentContext()
      isShipmentOwner = context.isShipmentOwner
    } catch {
      // Context not available, use default
    }

    const formattedLastSeen = lastSeen ? dateTimeAgo(lastSeen) : null
    const incorrectAuthorization =
      locationSettings?.authorizationStatus === AuthorizationStatusEnum.AuthorizationStatusAlways ? false : true

    const isLocationEnabled = locationSettings?.locationServicesEnabled === true
    const isGPSEnabled = locationSettings?.gpsGeolocationProviderAvailable === true
    const isPhoneValid = phoneNumberInfo?.isValid === true
    const isMobilePhone = phoneNumberInfo?.carrierType?.toLowerCase() === 'mobile'
    const phoneCarrier = phoneNumberInfo?.carrierName || 'Unknown carrier'

    return (
      <div>
        <div className="flex flex-col gap-2 items-start">
          {isHistoricalDevice ? (
            <HistoricalDeviceView
              device={device}
              driverName={driverName}
              formattedLastSeen={formattedLastSeen}
              locationSettings={locationSettings}
            />
          ) : (
            <ActiveDeviceView
              device={device}
              isTrackingOn={isTrackingOn}
              isLoggedIn={isLoggedIn}
              lastMotionActivity={lastMotionActivity}
              lastSeen={lastSeen}
              lastTrackingStoppedAt={lastTrackingStoppedAt}
              locationSettings={locationSettings}
              hasSentMockLocations={hasSentMockLocations}
              isShipmentOwner={isShipmentOwner}
            />
          )}

          <AlertSection
            isTrackingOn={isTrackingOn}
            incorrectAuthorization={incorrectAuthorization}
            isHistoricalDevice={isHistoricalDevice}
            lastSeen={lastSeen}
            locationSettings={locationSettings}
            device={device}
            lastMotionActivity={lastMotionActivity}
          />

          {!!phoneNumberInfo && (
            <div className="flex flex-col gap-1 border-t pt-2 w-full">
              <span className="text-foreground font-semibold">Phone Tracking</span>
              <span className="text-foreground text-xs">
                Location services: {isLocationEnabled ? 'enabled' : 'disabled'}
              </span>
              <span className="text-foreground text-xs">GPS provider: {isGPSEnabled ? 'enabled' : 'disabled'}</span>
              <span className="text-foreground text-xs">
                Number validity: {isPhoneValid && isMobilePhone ? 'valid mobile' : 'invalid or non-mobile'}
              </span>
              <span className="text-foreground text-xs">Carrier: {phoneCarrier}</span>
            </div>
          )}
        </div>
      </div>
    )
  }
)
